import { isPathExcluded, matchTool, redactUrl } from '../shared/match.ts';
import type {
  ErrorResponse,
  MarkedElement,
  PublicConfig,
  PublicTool,
  ReportPayload,
  ReportResponse,
  ReportType,
  WidgetSettings,
} from '../shared/types.ts';
import { isPageAccepted, serverOrigin } from './access.ts';
import { browserName, capturedErrors, collectEnvironment } from './context.ts';
import { h, icon, uid } from './dom.ts';
import { editScreenshot } from './editor.ts';
import { pickElements } from './picker.ts';
import { findFreeSpot, prefersLightLauncher, type Spot } from './placement.ts';
import { type Annotation, type Capture, captureDisplay, captureDom, compose, displayCaptureSupported, encode } from './screenshot.ts';
import { describeElement } from './selector.ts';
import { CSS } from './styles.ts';
import { T } from './texts.ts';

export interface WidgetOptions {
  config: PublicConfig;
  apiBase: string;
  rendererUrl: string;
  nonce?: string;
  /** Tool id set by the embedding page (data-tool) – wins over URL detection. */
  forcedToolId?: string;
  /** Settings from data-* attributes of the script tag. */
  overrides: Partial<WidgetSettings>;
  /** Extra paths without launcher from data-exclude (added to the configured ones). */
  excludePaths?: string[];
  privacyUrl?: string;
  version: string;
}

/** blocked: the page cannot send reports yet (see access.ts) – a notice instead of the form. */
type View = 'choose' | 'form' | 'success' | 'blocked';
type CaptureStatus = 'idle' | 'working' | 'ready' | 'failed';

interface FormState {
  type: ReportType;
  message: string;
  screenshotOn: boolean;
  /** The user ticked/unticked the checkbox themselves – keep that choice across report types. */
  screenshotChosen: boolean;
  capture?: Capture;
  captureStatus: CaptureStatus;
  capturePromise?: Promise<void>;
  annotations: Annotation[];
  picked: Element[];
  pickedInfo: MarkedElement[];
  openedAt: number;
}

const STORAGE_MINIMIZED = 'gsb:minimized';
const UNKNOWN_TOOL = '';

function storage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

/** The focused element, also inside open shadow roots (web components of the host page). */
function focusedElement(): HTMLElement | undefined {
  let el = document.activeElement;
  while (el?.shadowRoot?.activeElement) el = el.shadowRoot.activeElement;
  return el instanceof HTMLElement && el !== document.body ? el : undefined;
}

export class SupportBubble {
  readonly host: HTMLElement;
  private readonly options: WidgetOptions;
  private readonly config: PublicConfig;
  private readonly shadow: ShadowRoot;
  private readonly root: HTMLDivElement;
  private readonly launcher: HTMLButtonElement;
  private readonly minimizeBtn: HTMLButtonElement;
  private readonly panel: HTMLDivElement;
  private readonly toolLine: HTMLDivElement;
  private readonly body: HTMLDivElement;
  private readonly live: HTMLDivElement;
  private readonly titleId = uid('title');
  /** The server accepts reports from this page (checked locally, see access.ts). */
  private readonly accepted: boolean;

  private settings: WidgetSettings;
  private preferredSpot: Spot;
  private isOpen = false;
  /** Gets the focus back when the panel closes: launcher, data-gsb-open trigger or the API caller's focus. */
  private opener: HTMLElement | undefined;
  private minimized = false;
  private pathExcluded = false;
  private busy = false;
  private view: View = 'choose';
  private tool: PublicTool | undefined;
  private toolChosen = false;
  private editingTool = false;
  private form: FormState | undefined;
  private email = '';
  private user: { name?: string; email?: string } = {};
  private extra: Record<string, string> = {};
  private result: ReportResponse | undefined;
  private sending = false;
  /** The last send failed – offer to copy the typed text. */
  private sendFailed = false;
  private errorText = '';
  private placementTimer = 0;
  private readonly cleanups: Array<() => void> = [];

  // Form element references, valid while the form view is shown.
  private refs: {
    textarea?: HTMLTextAreaElement;
    email?: HTMLInputElement;
    honeypot?: HTMLInputElement;
    shotSlot?: HTMLDivElement;
    picksSlot?: HTMLDivElement;
    pickBtn?: HTMLButtonElement;
    errorSlot?: HTMLDivElement;
    copySlot?: HTMLDivElement;
    submit?: HTMLButtonElement;
    detailsSlot?: HTMLDListElement;
  } = {};

  constructor(options: WidgetOptions) {
    this.options = options;
    this.config = options.config;
    this.accepted = isPageAccepted(this.config, location.origin, serverOrigin(options.apiBase, location.href));
    // For whoever embeds the bubble: the panel only says "noch nicht freigeschaltet".
    if (!this.accepted) console.info(`[Green Support Bubble] ${location.origin} ist noch keinem Tool zugeordnet und nicht unter allowedOrigins eingetragen – Meldungen sind hier noch nicht möglich.`);
    this.tool = this.detectTool();
    this.settings = { ...this.config.widget, ...this.tool?.widget, ...options.overrides };
    this.preferredSpot = { position: this.settings.position, offsetX: this.settings.offsetX, offsetY: this.settings.offsetY };
    this.minimized = storage()?.getItem(STORAGE_MINIMIZED) === '1';

    this.host = document.createElement('green-support-bubble');
    const hostStyle: Record<string, string> = {
      all: 'initial',
      position: 'fixed',
      top: '0',
      left: '0',
      width: '0',
      height: '0',
      display: 'block',
      overflow: 'visible',
      'z-index': String(this.settings.zIndex),
    };
    for (const [prop, value] of Object.entries(hostStyle)) this.host.style.setProperty(prop, value, 'important');
    this.host.setAttribute('data-gsb-ignore', '');
    this.shadow = this.host.attachShadow({ mode: 'open' });
    this.adoptStyles();

    this.launcher = h('button', {
      class: 'gsb-launcher',
      attrs: { type: 'button', 'aria-expanded': 'false', 'aria-haspopup': 'dialog' },
      on: { click: () => this.onLauncherClick() },
    });
    this.minimizeBtn = h('button', {
      class: 'gsb-minimize',
      attrs: { type: 'button', 'aria-label': T.minimize, title: T.minimize },
      on: { click: () => this.setMinimized(true) },
    }, [icon('minimize', 14)]);

    this.toolLine = h('div', { class: 'gsb-toolline' });
    this.body = h('div', { class: 'gsb-body' });
    const closeBtn = h('button', { class: 'gsb-icon-btn', attrs: { type: 'button', 'aria-label': T.close, title: T.close }, on: { click: () => this.close() } }, [icon('close', 22)]);
    const footer = h('div', { class: 'gsb-foot' }, [
      options.privacyUrl ? h('a', { text: T.privacy, attrs: { href: options.privacyUrl, target: '_blank', rel: 'noopener' } }) : h('span'),
      h('span', { text: this.config.organisation }),
    ]);
    this.panel = h('div', { class: 'gsb-panel', attrs: { role: 'dialog', 'aria-modal': 'false', 'aria-labelledby': this.titleId, hidden: true } }, [
      h('div', { class: 'gsb-head' }, [
        h('div', { class: 'gsb-head-text' }, [h('h2', { class: 'gsb-title', text: T.title, attrs: { id: this.titleId, tabindex: '-1' } })]),
        closeBtn,
      ]),
      this.toolLine,
      this.body,
      footer,
    ]);
    this.panel.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.close();
      }
    });
    this.live = h('div', { class: 'gsb-sr', attrs: { 'aria-live': 'polite', 'aria-atomic': 'true' } });

    this.root = h('div', { class: 'gsb-root' }, [this.launcher, this.minimizeBtn, this.panel, this.live]);
    this.shadow.append(this.root);
    this.renderLauncher();
    this.applySpot(this.preferredSpot);
  }

  mount(): void {
    const body = document.body;
    const style = getComputedStyle(body);
    // A transform/filter on <body> would turn our position:fixed into position:absolute.
    const bodyBreaksFixed = style.transform !== 'none' || style.filter !== 'none' || style.perspective !== 'none';
    (bodyBreaksFixed ? document.documentElement : body).append(this.host);
    this.watchPlacement();
    this.watchNavigation();
    this.watchTriggers();
    this.watchTyping();
  }

  destroy(): void {
    this.cleanups.forEach((fn) => fn());
    this.host.remove();
  }

  // ---------- Public API ----------

  /** opener: what gets the focus back on close – defaults to the element focused right now. */
  open(type?: ReportType, opener?: HTMLElement): void {
    if (!this.isOpen) this.opener = opener ?? focusedElement();
    if (this.minimized) this.setMinimized(false);
    if (!this.toolChosen) this.tool = this.detectTool();
    if (this.view === 'success') this.view = 'choose';
    this.isOpen = true;
    this.setElevated(true);
    this.panel.hidden = false;
    this.root.classList.add('gsb-open');
    this.renderLauncher();
    this.renderToolLine();
    if (!this.accepted) this.showView('blocked');
    else if (type) this.startForm(type);
    else this.showView(this.view);
    this.announce(T.announceOpen);
  }

  close(): void {
    if (!this.isOpen) return;
    const focusWasInside = this.panel.contains(this.shadow.activeElement);
    const opener = this.opener;
    this.opener = undefined;
    this.isOpen = false;
    this.panel.hidden = true;
    this.root.classList.remove('gsb-open');
    this.setElevated(false);
    this.renderLauncher();
    if (focusWasInside) this.restoreFocus(opener);
    this.schedulePlacement();
  }

  toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  setUser(user: { name?: string; email?: string }): void {
    this.user = { name: user.name?.trim() || undefined, email: user.email?.trim() || undefined };
    if (!this.email && this.user.email) this.email = this.user.email;
    if (this.refs.email && !this.refs.email.value) this.refs.email.value = this.email;
  }

  setContext(values: Record<string, unknown>): void {
    for (const [key, value] of Object.entries(values)) {
      if (value === undefined || value === null) delete this.extra[key];
      else this.extra[key] = String(value).slice(0, 500);
    }
    this.renderDetails();
  }

  showLauncher(visible: boolean): void {
    this.settings.hidden = !visible;
    this.renderLauncher();
  }

  // ---------- Setup helpers ----------

  private adoptStyles(): void {
    try {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(CSS);
      this.shadow.adoptedStyleSheets = [sheet];
    } catch {
      // Older browsers: a <style> element (needs style-src for the nonce/CSP of the page).
      const style = document.createElement('style');
      if (this.options.nonce) style.nonce = this.options.nonce;
      style.textContent = CSS;
      this.shadow.append(style);
    }
  }

  private detectTool(): PublicTool | undefined {
    const tools = this.config.tools;
    if (this.options.forcedToolId) {
      const forced = tools.find((t) => t.id === this.options.forcedToolId);
      if (forced) return forced;
    }
    return matchTool(tools, location.href);
  }

  private contactLabel(): string {
    return this.tool?.contactLabel ?? (this.tool?.provider ? `Team ${this.tool.provider}` : this.config.fallbackContactLabel);
  }

  private screenshotSettings() {
    return { ...this.config.screenshot, ...this.tool?.screenshot };
  }

  /**
   * Back to where the user came from (WCAG 2.4.3). The opener may be gone (re-rendered by the
   * host app) or not focusable; then the launcher – unless it is hidden (data-hidden, excludePaths).
   */
  private restoreFocus(opener: HTMLElement | undefined): void {
    opener?.focus();
    if (opener?.matches(':focus')) return;
    if (this.launcher.getClientRects().length) this.launcher.focus();
  }

  private announce(message: string): void {
    this.live.textContent = '';
    // Re-setting on the next frame makes screen readers announce repeated messages too.
    requestAnimationFrame(() => (this.live.textContent = message));
  }

  // ---------- Launcher & placement ----------

  private onLauncherClick(): void {
    // A click on the minimised handle restores the bubble and opens it right away
    // (minimised implies closed). Safari does not focus clicked buttons, so name the opener.
    if (this.isOpen) this.close();
    else this.open(undefined, this.launcher);
  }

  private setMinimized(minimized: boolean): void {
    this.minimized = minimized;
    try {
      if (minimized) storage()?.setItem(STORAGE_MINIMIZED, '1');
      else storage()?.removeItem(STORAGE_MINIMIZED);
    } catch {
      // Storage may be blocked – minimising then only lasts for this page view.
    }
    this.renderLauncher();
    this.schedulePlacement();
    if (minimized) this.launcher.focus();
  }

  private renderLauncher(): void {
    const { launcher, root } = this;
    root.classList.toggle('gsb-launcher-tab', this.settings.launcher === 'tab');
    root.classList.toggle('gsb-launcher-bubble', this.settings.launcher !== 'tab');
    root.classList.toggle('gsb-minimized', this.minimized && !this.isOpen);
    root.classList.toggle('gsb-launcher-hidden', this.settings.hidden || this.pathExcluded);
    launcher.replaceChildren();
    launcher.setAttribute('aria-expanded', String(this.isOpen));
    if (this.minimized && !this.isOpen) {
      const right = this.preferredSpot.position.endsWith('right');
      launcher.append(icon(right ? 'chevronLeft' : 'chevronRight', 18), h('span', { class: 'gsb-launcher-label', text: T.restore }));
      launcher.setAttribute('aria-label', T.restore);
      launcher.title = T.restore;
    } else if (this.isOpen) {
      launcher.append(icon('close', 24), h('span', { class: 'gsb-launcher-label', text: T.launcher }));
      launcher.setAttribute('aria-label', T.launcherClose);
      launcher.title = T.launcherClose;
    } else {
      launcher.append(icon('feedback', 26), h('span', { class: 'gsb-launcher-label', text: T.launcher }));
      launcher.setAttribute('aria-label', T.launcherLabel);
      launcher.title = T.launcherLabel;
    }
  }

  /**
   * While the user works with the panel, picker or editor, lift the widget above everything:
   * into the browser's top layer (Popover API) where available, otherwise to the maximum
   * z-index. At rest it stays at the configured, lower z-index so host dialogs can cover it.
   */
  private setElevated(on: boolean): void {
    this.host.style.setProperty('z-index', String(on ? 2147483647 : this.settings.zIndex), 'important');
    if (typeof this.host.showPopover !== 'function') return;
    try {
      const isPopoverOpen = this.host.matches(':popover-open');
      if (on && !isPopoverOpen) {
        this.host.popover = 'manual';
        this.host.showPopover();
      } else if (!on && isPopoverOpen) {
        this.host.hidePopover();
        this.host.removeAttribute('popover');
      }
    } catch {
      // Older engines: the z-index fallback above applies.
    }
  }

  private applySpot(spot: Spot): void {
    for (const corner of ['bottom-right', 'bottom-left', 'top-right', 'top-left']) this.root.classList.remove(`gsb-pos-${corner}`);
    this.root.classList.add(`gsb-pos-${spot.position}`);
    this.root.style.setProperty('--gsb-x', `${spot.offsetX}px`);
    this.root.style.setProperty('--gsb-y', `${spot.offsetY}px`);
    this.root.style.setProperty('--gsb-z', String(this.settings.zIndex));
    this.host.dataset.position = spot.position;
  }

  private updatePlacement(): void {
    this.placementTimer = 0;
    if (this.isOpen || this.busy || this.settings.hidden || this.pathExcluded || !this.host.isConnected) return;
    // Don't move the launcher away under the user's pointer or keyboard focus.
    const engaged = this.launcher.matches(':hover') || this.shadow.activeElement === this.launcher;
    if (this.settings.autoAvoid && !engaged) {
      const rect = this.launcher.getBoundingClientRect();
      const size = { width: Math.max(rect.width, 40), height: Math.max(rect.height, 40) };
      const spot = this.minimized || this.settings.launcher === 'tab' ? this.preferredSpot : findFreeSpot(this.preferredSpot, size, this.host);
      this.applySpot(spot);
    }
    const rect = this.launcher.getBoundingClientRect();
    this.root.classList.toggle('gsb-on-dark', prefersLightLauncher(rect.left + rect.width / 2, rect.top + rect.height / 2, this.host));
  }

  private schedulePlacement(delay = 250): void {
    if (this.placementTimer) clearTimeout(this.placementTimer);
    this.placementTimer = window.setTimeout(() => this.updatePlacement(), delay);
  }

  private watchPlacement(): void {
    requestAnimationFrame(() => this.updatePlacement());
    // Chat widgets, cookie banners and theme changes often happen a little after page load.
    const timers = [1000, 3000, 8000].map((ms) => window.setTimeout(() => this.updatePlacement(), ms));
    const onResize = () => this.schedulePlacement();
    window.addEventListener('resize', onResize, { passive: true });
    // Tools that switch themes usually do it via a class or style on <html>/<body>.
    const themeObserver = new MutationObserver(() => this.schedulePlacement(300));
    for (const el of [document.documentElement, document.body]) themeObserver.observe(el, { attributes: true, attributeFilter: ['class', 'style', 'data-theme'] });
    this.cleanups.push(() => {
      timers.forEach(clearTimeout);
      window.removeEventListener('resize', onResize);
      themeObserver.disconnect();
    });
    if (!this.settings.autoAvoid) return;

    // Watch only the top level of <body>: that is where overlay widgets live, and it keeps
    // the observer cheap on busy single-page apps.
    const childObserver = new MutationObserver(() => this.schedulePlacement(500));
    const attrObserver = new MutationObserver(() => this.schedulePlacement(500));
    const observeChildren = () => {
      attrObserver.disconnect();
      for (const child of document.body.children) {
        if (child !== this.host) attrObserver.observe(child, { attributes: true, attributeFilter: ['style', 'class', 'hidden', 'open'] });
      }
    };
    childObserver.observe(document.body, { childList: true });
    observeChildren();
    const rewatch = new MutationObserver(observeChildren);
    rewatch.observe(document.body, { childList: true });

    this.cleanups.push(() => {
      childObserver.disconnect();
      attrObserver.disconnect();
      rewatch.disconnect();
    });
  }

  /** Union of global, per-tool and script-tag exclusions – all of them apply. */
  excludePatterns(): string[] {
    return [...(this.config.widget.excludePaths ?? []), ...(this.tool?.widget?.excludePaths ?? []), ...(this.options.excludePaths ?? [])];
  }

  /** True if the launcher is hidden on the current page because of excludePaths. */
  isExcludedHere(): boolean {
    return this.pathExcluded;
  }

  /**
   * Pages listed in excludePaths show no launcher (the JS API and data-gsb-open links keep
   * working). Single-page apps change the path without reloading, so re-check on navigation.
   */
  private watchNavigation(): void {
    const check = () => {
      const excluded = isPathExcluded(this.excludePatterns(), location.pathname);
      if (excluded === this.pathExcluded) return;
      this.pathExcluded = excluded;
      this.renderLauncher();
      if (!excluded) this.schedulePlacement();
    };
    check();
    if (!this.excludePatterns().length) return;
    window.addEventListener('popstate', check);
    window.addEventListener('hashchange', check);
    const timer = window.setInterval(check, 1500);
    this.cleanups.push(() => {
      window.removeEventListener('popstate', check);
      window.removeEventListener('hashchange', check);
      clearInterval(timer);
    });
  }

  /** Any element with data-gsb-open="bug|feature|" in the host page opens the widget. */
  private watchTriggers(): void {
    const onClick = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target.closest('[data-gsb-open]') : null;
      if (!target) return;
      event.preventDefault();
      const value = target.getAttribute('data-gsb-open');
      this.open(value === 'bug' || value === 'feature' ? value : undefined, target instanceof HTMLElement ? target : undefined);
    };
    document.addEventListener('click', onClick);
    this.cleanups.push(() => document.removeEventListener('click', onClick));
  }

  /**
   * On touch devices, step aside while someone types into the host page: some mobile browsers
   * shrink the viewport for the on-screen keyboard, which would push the launcher onto the
   * page's own buttons (e.g. "Absenden" right below the input).
   */
  private watchTyping(): void {
    const coarse = window.matchMedia?.('(pointer: coarse)');
    const isTextField = (el: EventTarget | null) =>
      el instanceof HTMLElement &&
      el !== this.host &&
      (el.isContentEditable || el.matches('textarea, select, input:not([type="checkbox"], [type="radio"], [type="button"], [type="submit"], [type="reset"], [type="range"], [type="color"], [type="file"])'));
    const onFocusIn = (event: FocusEvent) => {
      if (coarse?.matches && isTextField(event.target)) this.root.classList.add('gsb-typing');
    };
    const onFocusOut = () => this.root.classList.remove('gsb-typing');
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    this.cleanups.push(() => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
    });
  }

  // ---------- Panel views ----------

  private showView(view: View): void {
    this.view = view;
    this.refs = {};
    this.body.replaceChildren();
    if (view === 'choose') this.renderChoose();
    else if (view === 'form') this.renderForm();
    else if (view === 'blocked') this.renderBlocked();
    else this.renderSuccess();
    this.body.scrollTop = 0;
    if (view === 'form') this.refs.textarea?.focus();
  }

  private renderToolLine(): void {
    const line = this.toolLine;
    line.replaceChildren();
    // No tool question where nothing can be sent anyway.
    line.hidden = !this.accepted;
    if (line.hidden) return;
    const tools = this.config.tools;
    if (!this.tool || this.editingTool) {
      if (!tools.length) {
        line.hidden = true;
        return;
      }
      line.hidden = false;
      const selectId = uid('tool');
      const select = h('select', { class: 'gsb-select', attrs: { id: selectId } }, [
        h('option', { text: T.toolUnknown, attrs: { value: UNKNOWN_TOOL } }),
        ...tools.map((t) => h('option', { text: t.name + (t.provider ? ` (${t.provider})` : ''), attrs: { value: t.id } })),
      ]);
      select.value = this.tool?.id ?? UNKNOWN_TOOL;
      select.addEventListener('change', () => {
        this.tool = tools.find((t) => t.id === select.value);
        this.toolChosen = true;
        this.editingTool = false;
        this.renderToolLine();
        if (this.view === 'choose') this.showView('choose');
        (this.toolLine.querySelector('button') ?? this.toolLine.querySelector('select'))?.focus();
      });
      line.append(h('label', { text: T.toolSelectLabel, attrs: { for: selectId } }), select);
      return;
    }
    line.hidden = false;
    const changeBtn = h('button', {
      class: 'gsb-link',
      text: T.toolChange,
      attrs: { type: 'button', 'aria-label': `${T.toolDetected} ${this.tool.name} – ${T.toolChange}` },
      on: {
        click: () => {
          this.editingTool = true;
          this.renderToolLine();
          this.toolLine.querySelector('select')?.focus();
        },
      },
    });
    line.append(h('span', { text: `${T.toolDetected}:` }), h('strong', { text: this.tool.name }));
    if (this.tool.provider) line.append(h('span', { class: 'gsb-provider', text: T.toolProvider(this.tool.provider) }));
    if (tools.length > 1) line.append(changeBtn);
  }

  private renderChoose(): void {
    const choice = (type: ReportType, iconName: 'bug' | 'idea', title: string, hint: string) =>
      h('button', { class: 'gsb-choice', attrs: { type: 'button' }, on: { click: () => this.startForm(type) } }, [
        h('span', { class: 'gsb-choice-icon' }, [icon(iconName, 24)]),
        h('span', {}, [h('span', { class: 'gsb-choice-title', text: title }), h('span', { class: 'gsb-choice-hint', text: hint })]),
      ]);
    const first = choice('bug', 'bug', T.chooseBug, T.chooseBugHint);
    this.body.append(
      first,
      choice('feature', 'idea', T.chooseFeature, T.chooseFeatureHint),
      h('p', { class: 'gsb-recipient' }, [icon('info', 16), T.recipient(this.contactLabel())]),
    );
    if (this.tool?.helpUrl) {
      this.body.append(h('a', { class: 'gsb-help', text: T.help(this.tool.name), attrs: { href: this.tool.helpUrl, target: '_blank', rel: 'noopener' } }));
    }
    first.focus();
  }

  /** The page is not (yet) allowed to send reports: say so before anyone types – no form, no screenshot. */
  private renderBlocked(): void {
    const heading = h('h3', { class: 'gsb-h', text: T.blockedTitle, attrs: { tabindex: '-1' } });
    this.body.append(
      heading,
      h('p', { class: 'gsb-notice', text: T.blockedText }),
      h('p', { class: 'gsb-recipient' }, [icon('info', 16), T.blockedContact(this.config.fallbackContactLabel)]),
    );
    heading.focus();
  }

  private startForm(type: ReportType): void {
    const shot = this.screenshotSettings();
    if (!this.form || this.form.type !== type) {
      const previous = this.form;
      this.form = {
        type,
        message: previous?.message ?? '',
        screenshotOn: shot.enabled && (previous?.screenshotChosen ? previous.screenshotOn : type === 'bug' && shot.defaultOn),
        screenshotChosen: previous?.screenshotChosen ?? false,
        capture: previous?.capture,
        captureStatus: previous?.captureStatus ?? 'idle',
        capturePromise: previous?.capturePromise,
        annotations: previous?.annotations ?? [],
        picked: previous?.picked ?? [],
        pickedInfo: previous?.pickedInfo ?? [],
        openedAt: previous?.openedAt ?? Date.now(),
      };
    }
    this.errorText = '';
    this.sendFailed = false;
    this.showView('form');
    if (this.form.screenshotOn && this.form.captureStatus === 'idle') void this.capture('dom');
  }

  private renderForm(): void {
    const form = this.form as FormState;
    const type = form.type;
    const backBtn = h('button', { class: 'gsb-icon-btn', attrs: { type: 'button', 'aria-label': T.back, title: T.back }, on: { click: () => this.showView('choose') } }, [icon('back', 22)]);
    const head = h('div', { class: 'gsb-form-head' }, [backBtn, h('h3', { class: 'gsb-h', text: type === 'bug' ? T.chooseBug : T.chooseFeature })]);

    const messageId = uid('message');
    const hintId = uid('hint');
    const textarea = h('textarea', {
      class: 'gsb-textarea',
      attrs: { id: messageId, 'aria-describedby': hintId, required: true, maxlength: 5000, rows: 5 },
      on: { input: () => (form.message = textarea.value) },
    });
    textarea.value = form.message;
    const messageField = h('div', { class: 'gsb-field' }, [
      h('label', { class: 'gsb-label', text: T.messageLabel[type], attrs: { for: messageId } }),
      h('p', { class: 'gsb-hint', text: T.messageHint[type], attrs: { id: hintId } }),
      textarea,
    ]);

    const shotSlot = h('div');
    const picksSlot = h('div');
    const pickBtn = h('button', { class: 'gsb-btn gsb-btn-secondary gsb-btn-small', attrs: { type: 'button' }, on: { click: () => void this.pick() } }, [icon('target', 18), T.pickStart]);
    const attachments = h('div', { class: 'gsb-field' }, [h('p', { class: 'gsb-section-title', text: T.attachments[type] }), shotSlot, pickBtn, picksSlot]);

    const emailId = uid('email');
    const emailHint = uid('email-hint');
    const email = h('input', {
      class: 'gsb-input',
      attrs: { id: emailId, type: 'email', autocomplete: 'email', inputmode: 'email', 'aria-describedby': emailHint, maxlength: 254 },
      on: { input: () => (this.email = email.value) },
    });
    email.value = this.email;
    const emailField = h('div', { class: 'gsb-field' }, [
      h('label', { class: 'gsb-label', text: T.contactLabel, attrs: { for: emailId } }),
      h('p', { class: 'gsb-hint', text: T.contactHint, attrs: { id: emailHint } }),
      email,
    ]);

    const honeypot = h('input', { attrs: { type: 'text', name: 'website', tabindex: '-1', autocomplete: 'off' } });
    const detailsList = h('dl');
    const details = h('details', { class: 'gsb-details' }, [h('summary', { text: T.detailsSummary }), detailsList]);
    const errorSlot = h('div', { attrs: { role: 'alert' } });
    // Outside the alert, so the button is not read out with every error message.
    const copySlot = h('div');
    const submit = h('button', { class: 'gsb-btn gsb-btn-primary gsb-btn-block', attrs: { type: 'submit' } }, [T.submit]);

    const formEl = h('form', { attrs: { novalidate: true }, on: { submit: (event) => { event.preventDefault(); void this.submit(); } } }, [
      head,
      messageField,
      attachments,
      emailField,
      h('div', { class: 'gsb-honeypot', attrs: { 'aria-hidden': 'true' } }, [honeypot]),
      details,
      errorSlot,
      copySlot,
      submit,
    ]);
    this.body.append(formEl);
    this.refs = { textarea, email, honeypot, shotSlot, picksSlot, pickBtn, errorSlot, copySlot, submit, detailsSlot: detailsList };
    this.renderShot();
    this.renderPicks();
    this.renderDetails();
    this.renderError();
  }

  private renderShot(): void {
    const slot = this.refs.shotSlot;
    const form = this.form;
    if (!slot || !form) return;
    slot.replaceChildren();
    if (!this.screenshotSettings().enabled) return;

    const toggle = h('input', {
      attrs: { type: 'checkbox' },
      on: {
        change: () => {
          form.screenshotOn = toggle.checked;
          form.screenshotChosen = true;
          if (form.screenshotOn && form.captureStatus === 'idle') void this.capture('dom');
          else this.renderShot();
        },
      },
    });
    toggle.checked = form.screenshotOn;
    slot.append(h('label', { class: 'gsb-check' }, [toggle, T.screenshotToggle]));
    if (!form.screenshotOn) return;

    if (form.captureStatus === 'working') {
      slot.append(h('div', { class: 'gsb-shot gsb-shot-status' }, [h('span', { class: 'gsb-spinner', attrs: { 'aria-hidden': 'true' } }), T.screenshotWorking]));
      return;
    }
    if (form.captureStatus === 'failed' || !form.capture) {
      const children: Array<Node | string> = [h('span', { text: T.screenshotFailed })];
      const actions = h('span', { class: 'gsb-shot-actions' }, [
        h('button', { class: 'gsb-link', text: T.screenshotRetake, attrs: { type: 'button' }, on: { click: () => void this.capture('dom') } }),
      ]);
      if (displayCaptureSupported()) {
        actions.append(h('button', { class: 'gsb-link', text: T.screenshotDisplay, attrs: { type: 'button', title: T.screenshotDisplayHint }, on: { click: () => void this.capture('display') } }));
      }
      children.push(actions);
      slot.append(h('div', { class: 'gsb-shot' }, [h('div', {}, children)]));
      return;
    }

    const composed = compose(form.capture, form.pickedInfo.map((p) => p.rect), form.annotations);
    const thumb = document.createElement('canvas');
    const ratio = Math.min(1, 336 / composed.width);
    thumb.width = Math.max(1, Math.round(composed.width * ratio));
    thumb.height = Math.max(1, Math.round(composed.height * ratio));
    thumb.getContext('2d')?.drawImage(composed, 0, 0, thumb.width, thumb.height);
    thumb.setAttribute('aria-hidden', 'true');
    const thumbBtn = h('button', { class: 'gsb-thumb-btn', attrs: { type: 'button', 'aria-label': T.screenshotEditLabel, title: T.screenshotEditLabel }, on: { click: () => void this.edit() } }, [thumb]);
    slot.append(
      h('div', { class: 'gsb-shot' }, [
        thumbBtn,
        h('div', {}, [
          h('span', { text: T.screenshotReady }),
          h('span', { class: 'gsb-shot-actions' }, [
            h('button', { class: 'gsb-link', text: T.screenshotEdit, attrs: { type: 'button', 'aria-label': T.screenshotEditLabel }, on: { click: () => void this.edit() } }),
            h('button', { class: 'gsb-link', text: T.screenshotRetake, attrs: { type: 'button' }, on: { click: () => void this.capture('dom') } }),
          ]),
        ]),
      ]),
    );
  }

  private renderPicks(): void {
    const slot = this.refs.picksSlot;
    const form = this.form;
    if (!slot || !form) return;
    slot.replaceChildren();
    if (this.refs.pickBtn) {
      this.refs.pickBtn.replaceChildren(icon('target', 18), form.picked.length ? T.pickMore : T.pickStart);
    }
    if (!form.pickedInfo.length) return;
    const list = h('ol', { class: 'gsb-picks' });
    form.pickedInfo.forEach((info, index) => {
      const label = info.label ?? `<${info.tag}>`;
      const remove = h('button', {
        class: 'gsb-icon-btn',
        attrs: { type: 'button', 'aria-label': T.pickRemove(label), title: T.pickRemove(label) },
        on: {
          click: () => {
            form.picked.splice(index, 1);
            form.pickedInfo.splice(index, 1);
            this.renderPicks();
            this.renderShot();
            this.refs.pickBtn?.focus();
          },
        },
      }, [icon('trash', 16)]);
      list.append(h('li', {}, [h('span', { class: 'gsb-num', text: String(index + 1), attrs: { 'aria-hidden': 'true' } }), h('span', { class: 'gsb-pick-text', text: label, attrs: { title: info.selector } }), remove]));
    });
    slot.append(list);
  }

  private renderDetails(): void {
    const list = this.refs.detailsSlot;
    if (!list) return;
    list.replaceChildren();
    const errors = capturedErrors();
    const rows: Array<[string, string]> = [
      [T.detailsPage, redactUrl(location.href, this.config.privacy.redactQueryParams)],
      [T.detailsBrowser, browserName()],
      [T.detailsWindow, `${window.innerWidth} × ${window.innerHeight} px`],
      [T.detailsLanguage, navigator.language],
      [T.detailsErrors, errors.length ? String(errors.length) : T.detailsNoErrors],
    ];
    if (Object.keys(this.extra).length) rows.push([T.detailsExtra, Object.keys(this.extra).join(', ')]);
    for (const [term, value] of rows) list.append(h('dt', { text: term }), h('dd', { text: value }));
  }

  private renderError(): void {
    const { errorSlot, copySlot } = this.refs;
    errorSlot?.replaceChildren(...(this.errorText ? [h('p', { class: 'gsb-error', text: this.errorText })] : []));
    copySlot?.replaceChildren();
    if (!copySlot || !this.sendFailed) return;
    // A failed send must never cost the typed text: the form stays as it is, and the text can be copied.
    const hint = h('p', { class: 'gsb-hint', text: T.copyHint });
    const button = h('button', { class: 'gsb-btn gsb-btn-secondary gsb-btn-small', text: T.copyText, attrs: { type: 'button' } });
    button.addEventListener('click', () => {
      void this.copyMessage(button).then((copied) => {
        button.textContent = copied ? T.copied : T.copyText;
        hint.textContent = copied ? T.copyHint : T.copyFailed;
      });
    });
    copySlot.append(h('div', { class: 'gsb-copy' }, [hint, button]));
  }

  /** Clipboard API first; where it is missing or refused (http pages, permissions), copy the selected text field. */
  private async copyMessage(button: HTMLButtonElement): Promise<boolean> {
    let copied = false;
    try {
      await navigator.clipboard.writeText(this.form?.message ?? '');
      copied = true;
    } catch {
      const textarea = this.refs.textarea;
      if (textarea) {
        textarea.focus();
        textarea.select();
        try {
          copied = document.execCommand('copy');
        } catch {
          // Not supported.
        }
        // Copied: deselect and step back, so the next key press cannot replace the text.
        // Otherwise it stays selected for Ctrl+C.
        if (copied) {
          textarea.setSelectionRange(textarea.value.length, textarea.value.length);
          button.focus();
        }
      }
    }
    this.announce(copied ? T.copied : T.copyFailed);
    return copied;
  }

  private renderSuccess(): void {
    const result = this.result as ReportResponse;
    const copyBtn = h('button', { class: 'gsb-link', text: T.successCopy, attrs: { type: 'button' } });
    copyBtn.addEventListener('click', () => {
      void navigator.clipboard?.writeText(result.id).then(() => {
        copyBtn.textContent = T.successCopied;
        this.announce(T.successCopied);
      });
    });
    const heading = h('h3', { class: 'gsb-h', text: T.successTitle, attrs: { tabindex: '-1' } });
    this.body.append(
      h('div', { class: 'gsb-success' }, [
        h('div', { class: 'gsb-success-icon', attrs: { 'aria-hidden': 'true' } }, [icon('check', 34)]),
        heading,
        h('p', { text: T.successText(result.contactLabel) }),
        h('div', { class: 'gsb-ref' }, [
          h('span', { class: 'gsb-ref-label', text: T.successId }),
          h('span', { class: 'gsb-ref-id', text: result.id }),
          navigator.clipboard ? copyBtn : null,
        ]),
        h('div', { class: 'gsb-actions' }, [
          h('button', { class: 'gsb-btn gsb-btn-secondary', text: T.successNew, attrs: { type: 'button' }, on: { click: () => this.showView('choose') } }),
          h('button', { class: 'gsb-btn gsb-btn-primary', text: T.successClose, attrs: { type: 'button' }, on: { click: () => this.close() } }),
        ]),
      ]),
    );
    heading.focus();
  }

  // ---------- Actions ----------

  private setBusy(busy: boolean): void {
    this.busy = busy;
    this.root.classList.toggle('gsb-busy', busy);
  }

  private capture(method: 'dom' | 'display'): Promise<void> {
    const form = this.form;
    if (!form) return Promise.resolve();
    form.captureStatus = 'working';
    form.annotations = [];
    this.renderShot();
    const redact = this.config.privacy.redactSelectors;
    let pending: Promise<Capture>;
    if (method === 'display') {
      // Hide our own UI synchronously: getDisplayMedia must be called within the click.
      this.setBusy(true);
      pending = captureDisplay(redact).finally(() => this.setBusy(false));
    } else {
      pending = captureDom({ rendererUrl: this.options.rendererUrl, nonce: this.options.nonce, redactSelectors: redact, host: this.host });
    }
    const promise = pending.then(
      (capture) => {
        if (this.form !== form) return;
        form.capture = capture;
        form.captureStatus = 'ready';
        this.announce(T.screenshotReady);
      },
      (err: unknown) => {
        if (this.form !== form) return;
        console.warn('[Green Support Bubble] Screenshot fehlgeschlagen:', err);
        form.capture = undefined;
        form.captureStatus = 'failed';
        this.announce(T.screenshotFailed);
      },
    ).finally(() => {
      if (this.form === form) this.renderShot();
    });
    form.capturePromise = promise;
    return promise;
  }

  private async pick(): Promise<void> {
    const form = this.form;
    if (!form) return;
    this.setBusy(true);
    const result = await pickElements({ mount: this.root, host: this.host, initial: form.picked, banner: T.pickBanner[form.type], announce: (m) => this.announce(m) });
    this.setBusy(false);
    if (result) {
      form.picked = result;
      form.pickedInfo = result.map((el) => describeElement(el));
      this.renderPicks();
      this.renderShot();
      this.announce(T.pickCount(result.length));
    }
    this.refs.pickBtn?.focus();
  }

  private async edit(): Promise<void> {
    const form = this.form;
    if (!form?.capture) return;
    // The editor is modal: take the panel out of the tab order and accessibility tree meanwhile.
    this.panel.inert = true;
    form.annotations = await editScreenshot({
      mount: this.root,
      capture: form.capture,
      elementRects: form.pickedInfo.map((p) => p.rect),
      annotations: form.annotations,
    });
    this.panel.inert = false;
    this.renderShot();
    this.refs.shotSlot?.querySelector<HTMLButtonElement>('.gsb-thumb-btn')?.focus();
  }

  private fail(message: string, focus?: HTMLElement): void {
    this.errorText = message;
    this.renderError();
    focus?.focus();
  }

  private async submit(): Promise<void> {
    const form = this.form;
    if (!form || this.sending) return;
    const { textarea, email, submit } = this.refs;
    this.sendFailed = false;
    textarea?.setAttribute('aria-invalid', 'false');
    email?.setAttribute('aria-invalid', 'false');

    const message = form.message.trim();
    if (message.length < 5) {
      textarea?.setAttribute('aria-invalid', 'true');
      this.fail(T.messageTooShort, textarea);
      return;
    }
    const contactEmail = this.email.trim();
    if (contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) {
      email?.setAttribute('aria-invalid', 'true');
      this.fail(T.contactInvalid, email);
      return;
    }

    this.sending = true;
    this.errorText = '';
    this.renderError();
    if (submit) {
      submit.disabled = true;
      submit.textContent = T.sending;
    }

    try {
      if (form.screenshotOn && form.captureStatus === 'working') await form.capturePromise;
      const screenshot = form.screenshotOn && form.capture ? encode(compose(form.capture, form.pickedInfo.map((p) => p.rect), form.annotations)) : undefined;
      // Descriptions from picking time – their positions match the numbered markers in the screenshot.
      const elements = form.pickedInfo;

      const payload: ReportPayload = {
        type: form.type,
        toolId: this.tool?.id,
        message,
        contact: contactEmail || this.user.name ? { name: this.user.name, email: contactEmail || undefined } : undefined,
        page: { url: redactUrl(location.href, this.config.privacy.redactQueryParams), title: document.title },
        screenshot,
        elements: elements.length ? elements : undefined,
        environment: collectEnvironment(this.options.version),
        errors: capturedErrors(),
        extra: Object.keys(this.extra).length ? { ...this.extra } : undefined,
        website: this.refs.honeypot?.value ?? '',
        elapsedMs: Date.now() - form.openedAt,
      };

      const response = await fetch(`${this.options.apiBase}/api/v1/reports`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        // The page address is in the report already (cleaned of tracking parameters); a Referer would repeat it uncleaned.
        referrerPolicy: 'no-referrer',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => undefined)) as ErrorResponse | undefined;
        // Same-origin/proxy installations pass the local check (access.ts) but may still be refused.
        const blocked = body?.error === 'origin_not_allowed' ? `${T.blockedText} ${T.blockedContact(this.config.fallbackContactLabel)}` : undefined;
        throw new Error(blocked ?? body?.message ?? T.networkError);
      }
      this.result = (await response.json()) as ReportResponse;
      this.form = undefined;
      this.showView('success');
      this.announce(`${T.successTitle} ${T.successId}: ${this.result.id}`);
    } catch (err) {
      const text = err instanceof TypeError ? T.networkError : (err as Error).message || T.networkError;
      if (submit) {
        submit.disabled = false;
        submit.textContent = T.submit;
      }
      this.sendFailed = true;
      this.fail(text, submit);
    } finally {
      this.sending = false;
    }
  }
}
